import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';
import {
  Checkbox,
  Table,
  Tabs,
  Select,
  Typography,
  TYPOGRAPHY_WEIGHT,
  Divider,
  notification,
  NOTIFICATION_TYPES,
  Input,
} from '_atoms';
import { TableMobile } from '_atoms/Table/Table';
import { PLACEMENTS, SelectSmall } from '_atoms/SelectSmall';
import type { SelectSmallOption } from '_atoms/SelectSmall';
import type { SelectOption as AtomSelectOption } from '_atoms/Select/types';
import type { TableColumnType, TableRow } from '_atoms/Table/types';
import type { Opening, InterviewFeedback } from 'store/reducers/jobReducer';
import type {
  DashboardSelectOption as SelectOption,
  DashboardTableOptions as TableOptions,
  DashboardGroupingOption as GroupingOption,
  OpeningDeclinePopupState,
  OpeningApprovePopupState,
} from '_templates/dashboardTypes';
import { SELECT_TYPES } from '_atoms/Select/constants';
import {
  PopupApproveOpening,
  PopupCloseDeclineOpening,
  PopupCreateInterview,
  PopupSubmitDecision,
  PopupSubmitOfferDecision,
} from '_molecules';
import { NewJobs, SelfSubmittedCandidatesDashboard } from '_organisms';
import { ReactComponent as Vector } from 'dist/icons/Vector1.svg';
import {
  createInterview,
  declineApproveOpening,
  getAllCompanies,
  getDocumentsByUUID,
  getCandidatesTableDashboardAPI,
  getHRLJobsTableDashboardAPI,
  getHRLServicesTableDashboardAPI,
  getInterviewFeedbacksDashboardAPI,
  improveInterviewFeedbackComment,
  getListUsersByRole,
  saveInterviewFeedbacks,
  submitDecision,
  submitOffer,
  submitDecisionWithOppening,
  getHiredCandidate,
  getOpening,
  ImproveInterviewFeedbackCommentPayload,
} from 'actions';
import useScreenResize from 'helpers/useScreenResize';
import { useQuery } from 'helpers/useLocation';
import { utcTime } from 'helpers/dateFormatter';
import { getCompanies } from 'store/selectors/commonSelectors';
import { getUser } from 'store/selectors/authorizeSelectors';
import type { AppDispatch } from 'store';
import { ReactComponent as JobImage } from 'dist/emptyStates/dashboardOpening.svg';
import { ROLES } from 'permission';
import { useInterviewData } from '_organisms/Interview/utils';
import { PopupCreateFeedback } from '_molecules/PopupCreateFeedback';
import { PopupCreateOffer } from '_molecules/PopupCreateOffer';
import { EXPECTED_DATES } from '_templates/CreateOpening/constants';
import { CANDIDATE_ACTION_OPTIONS, STEPS } from 'constants/constants';
import {
  jobTableDataMapper,
  serviceTableDataMapper,
  useTabs,
  isServiceGrouping,
  CANDIDATES_TABLES_MAPPERS,
} from './utils';
import {
  INITIAL_VALUES,
  COLUMNS_OPENINGS_SERVICE,
  INITIAL_VALUES_1,
  OPENINGS_GROUP,
  COLUMNS_OPENINGS_SERVICE_TOP,
  COLUMNS_OPENINGS_JOB_TOP,
  INITIAL_VALUES_2,
  COLUMNS_OPENINGS_JOB,
  OPENING_STATUSES_OPTIONS,
  CANDIDATES_GROUP,
  CANDIDATES_TABLE_COLUMNS,
  COMPANY_ALL,
} from './constants';
import './style.scss';

interface AllOpeningsServiceProps {
  setTabsLoading: (loading: boolean) => void;
  onChangeTrigger: (trigger: Date) => void;
}

interface MyActiveCandidatesProps {
  leadDashboard?: boolean;
  recruiterDashboard?: boolean;
  setTabsLoading: (loading: boolean) => void;
}

type CandidateFilters = {
  recruiters: SelectOption[];
  actions: SelectOption[];
  steps: SelectOption[];
  only_with_actions: boolean;
};

interface SelfSubmittedCandidatesDashboardTabProps {
  setTabsLoading: (loading: boolean) => void;
  dashboard: string;
}

const SelfSubmittedCandidatesDashboardTab =
  SelfSubmittedCandidatesDashboard as React.FC<SelfSubmittedCandidatesDashboardTabProps>;

function OpeningsTableEmpty() {
  const { t } = useTranslation();
  return (
    <div className="all-openings__empty">
      <JobImage />
      <Typography.Title3 weight={TYPOGRAPHY_WEIGHT.BOLD}>
        {t('translation:dashboard_hr_lead.text_no_openings')}
      </Typography.Title3>
      <Typography.Text>{t('translation:dashboard_hr_lead.text_no_openings_hint')}</Typography.Text>
    </div>
  );
}

function ActiveCandidatesTableEmpty() {
  const { t } = useTranslation();
  return (
    <div className="active-candidates__empty">
      <JobImage />
      <Typography.Title3 weight={TYPOGRAPHY_WEIGHT.BOLD}>
        {t('translation:dashboard_hr_lead.text_no_active_candidates')}
      </Typography.Title3>
      <Typography.Text>{t('translation:dashboard_hr_lead.text_no_active_candidates_hint')}</Typography.Text>
    </div>
  );
}

function AllOpeningsService({ setTabsLoading, onChangeTrigger }: AllOpeningsServiceProps) {
  const { t } = useTranslation();

  const dispatch = useDispatch<AppDispatch>();
  const tableRef = useRef<HTMLDivElement>(null);
  const tableRequestIdRef = useRef(0);

  const [loading, setLoading] = useState(false);
  const [tableData, setTableData] = useState<Record<string, unknown>[]>([]);
  const [tableOptions, setTableOptions] = useState<TableOptions>(INITIAL_VALUES_1);
  const [openingStatus, setOpeningStatus] = useState<SelectOption[]>([OPENING_STATUSES_OPTIONS[3]]);
  const [company, setCompany] = useState<SelectSmallOption>(COMPANY_ALL);
  const [grouping, setGrouping] = useState<GroupingOption>({
    ...OPENINGS_GROUP[0],
    tableData: OPENINGS_GROUP[1].tableData,
  } as GroupingOption);
  const [expandedRowKeys, setExpandedRowKeys] = useState<string[]>([]);
  const [showOpeningDeclinePopup, setShowOpeningDeclinePopup] = useState<OpeningDeclinePopupState>({
    open: false,
  });
  const [openApprovePopup, setOpenApprovePopup] = useState<OpeningApprovePopupState>({ open: false });

  const companies = useSelector(getCompanies);
  const companyOptions = useMemo(
    () => [COMPANY_ALL, ...companies.map(c => ({ value: c.uuid, label: c.name }))],
    [companies]
  );

  function getTableData(group: GroupingOption = grouping, options: TableOptions = tableOptions) {
    tableRequestIdRef.current += 1;
    const requestId = tableRequestIdRef.current;
    setLoading(true);
    setTabsLoading(true);
    if (isServiceGrouping(group.value)) {
      getHRLServicesTableDashboardAPI({
        limit: options.limit.value,
        offset: options.offset,
        status: openingStatus.map(({ value }) => value).join(','),
        company: company.value,
      })
        .then(d => {
          if (requestId !== tableRequestIdRef.current) return;
          setTableOptions(o =>
            group.value !== group.tableData
              ? { ...INITIAL_VALUES_1, itemsCount: d.count }
              : { ...o, itemsCount: d.count }
          );
          const data = serviceTableDataMapper(d.results);
          setTableData(data as Record<string, unknown>[]);
          setGrouping(g => ({ ...g, tableData: g.value }));
          setExpandedRowKeys(data.map(({ uuid }) => uuid as string));
        })
        .catch(() => {
          if (requestId !== tableRequestIdRef.current) return;
          setTableOptions(INITIAL_VALUES_1);
          setTableData([]);
          setGrouping(g => ({ ...g, tableData: g.value }));
        })
        .finally(() => {
          if (requestId !== tableRequestIdRef.current) return;
          setLoading(false);
          setTabsLoading(false);
        });
    } else {
      getHRLJobsTableDashboardAPI({
        limit: options.limit.value,
        offset: options.offset,
        status: openingStatus.map(({ value }) => value).join(','),
        company: company.value,
      })
        .then(d => {
          if (requestId !== tableRequestIdRef.current) return;
          setTableOptions(o =>
            group.value !== group.tableData
              ? { ...INITIAL_VALUES_2, itemsCount: d.count }
              : { ...o, itemsCount: d.count }
          );
          const data = jobTableDataMapper(d.results);
          setTableData(data as Record<string, unknown>[]);
          setGrouping(g => ({ ...g, tableData: g.value }));
          setExpandedRowKeys(data.map(({ uuid }) => uuid as string));
        })
        .catch(() => {
          if (requestId !== tableRequestIdRef.current) return;
          setTableOptions(INITIAL_VALUES_2);
          setTableData([]);
          setGrouping(g => ({ ...g, tableData: g.value }));
        })
        .finally(() => {
          if (requestId !== tableRequestIdRef.current) return;
          setLoading(false);
          setTabsLoading(false);
        });
    }
  }

  const onExpand =
    ({ uuid }: Record<string, unknown>) =>
    () => {
      const rowUuid = uuid as string;
      if (!expandedRowKeys.includes(rowUuid)) setExpandedRowKeys(rows => [...rows, rowUuid]);
      else setExpandedRowKeys(rows => rows.filter(r => r !== rowUuid));
    };

  const onClickTitle =
    ({ uuid, name }: Record<string, unknown>) =>
    () => {
      if (isServiceGrouping(grouping.value))
        window.open(`/jobs?services=[{"value":"${uuid}","label":"${name}"}]`, '_blank', 'noopener noreferrer');
      else window.open(`/jobs/${uuid}/show/openings`, '_blank', 'noopener noreferrer');
    };

  const onChangeGrouping = (v: GroupingOption) => {
    setGrouping(v);
    getTableData(v);
  };

  const onChangeFilter = (field: string, item: unknown) => {
    if (field === 'page') {
      const options = {
        ...tableOptions,
        page: item as number,
        offset: ((item as number) - 1) * (tableOptions.limit.value as number),
      };
      setTableOptions(options);
      getTableData(grouping, options);
    } else if (field === 'limit') {
      const options = { ...tableOptions, limit: item as { value: number; label: number }, offset: 0, page: 1 };
      setTableOptions(options);
      getTableData(grouping, options);
    } else {
      const options = { ...tableOptions, [field]: item };
      setTableOptions(options);
      getTableData(grouping, options);
    }
  };

  const onOpeningAction = ({ job_uuid, service_uuid, opening, ...d }: Record<string, unknown>, type: string) => {
    const openingData = opening as Record<string, unknown>;
    if (type === 'approve') {
      const show =
        Math.abs(moment(openingData.expected as string, 'YYYY-MM-DD').diff(moment(), 'days')) <
        Math.abs(EXPECTED_DATES[openingData.level as string]?.diff(moment(), 'days'));
      if (show) {
        setOpenApprovePopup({
          open: true,
          job_uuid: job_uuid as string,
          service_uuid: service_uuid as string,
          type,
        });
      } else {
        declineApproveOpening(job_uuid as string, service_uuid as string, type, {}).then(() => {
          getTableData();
          onChangeTrigger(new Date());
        });
      }
    } else {
      setShowOpeningDeclinePopup({
        open: true,
        job_uuid: job_uuid as string,
        service_uuid: service_uuid as string,
        opening: opening as Opening,
        type,
        job_name: d.job_name as string | undefined,
      });
    }
  };

  const onCloseCloseOpeningPopup = () => setShowOpeningDeclinePopup({ open: false });
  const onDeclineOpening = (reason: string) => {
    if (!showOpeningDeclinePopup.open) return;
    const { job_uuid, service_uuid, type } = showOpeningDeclinePopup;
    declineApproveOpening(job_uuid, service_uuid, type, { reason }).then(() => getTableData());
    setShowOpeningDeclinePopup({ open: false });
  };

  const onCloseApproveConfirmation = () => setOpenApprovePopup({ open: false });
  const onApproveConfirmationPopup = () => {
    if (!openApprovePopup.open) return;
    declineApproveOpening(openApprovePopup.job_uuid, openApprovePopup.service_uuid, openApprovePopup.type, {}).then(
      () => {
        getTableData();
        onChangeTrigger(new Date());
      }
    );
    setOpenApprovePopup({ open: false });
  };

  useEffect(() => {
    dispatch(getAllCompanies());
  }, [dispatch]);
  useEffect(() => {
    getTableData();
  }, [openingStatus, company]);
  useScreenResize('md', tableRef);

  return (
    <>
      <Table
        innerRef={tableRef}
        rowKey="uuid"
        className={cn('all-openings', grouping.tableData)}
        cardTitle={t('translation:dashboard_hr_lead.title')}
        cardCount={tableOptions.itemsCount}
        emptyComponent={<OpeningsTableEmpty />}
        extra={
          <>
            <Select
              labelInValue
              placeholder={t('translation:dashboard_hr_lead.ph_select_status')}
              options={OPENING_STATUSES_OPTIONS}
              value={openingStatus}
              onChange={setOpeningStatus}
              mode={SELECT_TYPES.MULTIPLE}
            />
            <div className="filter-selectors">
              <div className="filter-container">
                <SelectSmall
                  label={t('translation:dashboard_hr_lead.label_company')}
                  options={companyOptions}
                  value={company}
                  onChange={(option: SelectSmallOption) => setCompany(option)}
                  placement={PLACEMENTS.BOTTOM_RIGHT}
                />
              </div>
              <Divider type="vertical" />
              <div className="filter-container">
                <SelectSmall
                  label={t('translation:dashboard_hr_lead.label_group_by')}
                  options={OPENINGS_GROUP}
                  value={grouping}
                  onChange={onChangeGrouping}
                  placement={PLACEMENTS.BOTTOM_RIGHT}
                />
              </div>
            </div>
          </>
        }
        showCardFooter={!!tableData.length}
        columns={isServiceGrouping(grouping.tableData) ? COLUMNS_OPENINGS_SERVICE_TOP : COLUMNS_OPENINGS_JOB_TOP}
        loading={loading}
        data={tableData as TableRow[]}
        limit={tableOptions.limit}
        page={tableOptions.page}
        itemsCount={tableOptions.itemsCount}
        updateParams={onChangeFilter}
        expandable={{
          expandedRowKeys,
          // eslint-disable-next-line react/no-unstable-nested-components
          expandedRowRender: ({ openings }: Record<string, unknown>) => (
            <>
              <Table
                rowKey="uuid"
                className={cn('all-openings__sub-table', grouping.tableData)}
                columns={
                  isServiceGrouping(grouping.tableData)
                    ? COLUMNS_OPENINGS_SERVICE(onOpeningAction)
                    : COLUMNS_OPENINGS_JOB(onOpeningAction)
                }
                data={openings as TableRow[]}
                showCardHeader={false}
                showCardFooter={false}
              />
              <TableMobile
                className={cn('all-openings__sub-table all-openings__sub-table-mobile', grouping.tableData)}
                data={openings as TableRow[]}
                columns={
                  isServiceGrouping(grouping.tableData)
                    ? COLUMNS_OPENINGS_SERVICE(onOpeningAction)
                    : COLUMNS_OPENINGS_JOB(onOpeningAction)
                }
              />
            </>
          ),
          // eslint-disable-next-line react/no-unstable-nested-components
          expandIcon: ({ expanded, record }) => (
            <div role="none" className="expanded-row">
              <div className="expanded-row__info">
                <Vector className={cn('expanded-row__icon', { expanded })} onClick={onExpand(record)} />
                <Typography.Text className="name" onClick={onClickTitle(record)}>
                  {record.name}
                  <span>{record.count}</span>
                </Typography.Text>
                {!!record.service && <Typography.Text className="name__extra">{record.service}</Typography.Text>}
              </div>
              <div className="expanded-row__extra">
                <Typography.Caption>
                  {(t as (key: string, options?: Record<string, unknown>) => string)(
                    'translation:dashboard_hr_lead.text_total_active_candidates',
                    { count: record.candidates_count as number }
                  )}
                </Typography.Caption>
                <Typography.Caption>
                  {(t as (key: string, options?: Record<string, unknown>) => string)(
                    'translation:dashboard_hr_lead.text_total_openings',
                    { count: record.openings_count as number }
                  )}
                </Typography.Caption>
              </div>
            </div>
          ),
        }}
      />
      <PopupCloseDeclineOpening
        open={showOpeningDeclinePopup.open}
        opening={showOpeningDeclinePopup.open ? showOpeningDeclinePopup.opening : undefined}
        jobName={showOpeningDeclinePopup.open ? showOpeningDeclinePopup.job_name ?? '' : ''}
        onClose={onCloseCloseOpeningPopup}
        confirm={onDeclineOpening}
      />
      <PopupApproveOpening
        open={openApprovePopup.open}
        onOk={onApproveConfirmationPopup}
        onClose={onCloseApproveConfirmation}
      />
    </>
  );
}

export function MyActiveCandidates({ leadDashboard, recruiterDashboard, setTabsLoading }: MyActiveCandidatesProps) {
  const dispatch = useDispatch<AppDispatch>();

  const dashboard = window.location.pathname.split('/').pop()!.replace('_', '-');

  const tableRef = useRef<HTMLDivElement>(null);
  const user = useSelector(getUser)!;
  const [loading, setLoading] = useState(false);
  const [tableData, setTableData] = useState<Record<string, unknown>[]>([]);
  const [tableOptions, setTableOptions] = useState(INITIAL_VALUES);
  const [grouping, setGrouping] = useState<GroupingOption>(
    (CANDIDATES_GROUP.find(group => group.value === sessionStorage.getItem('activeCandidatesGrouping')) ||
      CANDIDATES_GROUP[0]) as GroupingOption
  );
  const [expandedRowKeys, setExpandedRowKeys] = useState<(string | number)[]>([]);
  const [filters, setFilters] = useState<CandidateFilters>({
    recruiters: leadDashboard ? [{ value: user.uuid, label: user.full_name }] : [],
    actions: [],
    steps: [],
    only_with_actions: false,
  });
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const normalizedSearch = debouncedSearch.trim();
  const searchQuery = normalizedSearch.length >= 2 ? normalizedSearch : '';
  const [recruitersOptions, setRecruitersOptions] = useState<SelectOption[]>([]);
  const [expandedCVS, setExpandedCVS] = useState<Record<string, boolean>>({});

  const [decisionPopup, setDecisionPopup] = useState<Record<string, unknown>>({
    open: false,
    activeStep: 0,
    isFinalDecision: false,
    userData: {},
    jobId: '',
    stepId: '',
    candidateId: '',
    body: {},
    link: '',
  });

  const interviewInitialValues = useInterviewData({}, user.profile.has_calendar_access);
  const [interviewPopup, setInterviewPopup] = useState<Record<string, unknown>>({
    open: false,
    empty: true,
    candidateName: '',
    jobId: '',
    stepId: '',
    candidateId: '',
    userData: {},
  });
  const [feedbackPopup, setFeedbackPopup] = useState<Record<string, unknown>>({
    open: false,
    feedback: undefined,
    userData: {},
    jobId: '',
    stepId: '',
    candidateId: '',
    scorecardId: '',
  });
  const [offerPopup, setOfferPopup] = useState<Record<string, unknown>>({
    open: false,
    userData: {},
    jobId: '',
    stepId: '',
    candidateId: '',
  });
  const [offerDecisionPopup, setOfferDecisionPopup] = useState<Record<string, unknown>>({
    open: false,
    userData: {},
    activeStep: 0,
    jobId: '',
    stepId: '',
    candidateId: '',
  });

  const onChangeFilter = (field: string, item: unknown) => {
    if (field === 'page') {
      setTableOptions(s => ({ ...s, page: item as number, offset: ((item as number) - 1) * s.limit.value }));
    } else if (field === 'limit') {
      setTableOptions(s => ({ ...s, limit: item as { value: number; label: number }, offset: 0, page: 1 }));
    } else {
      setTableOptions(s => ({ ...s, [field]: item }));
    }
  };
  const setSorting = (sort: { field: string; key: string; order: string }) => onChangeFilter('sorting', sort);
  const onCandidateFilterChange = (field: keyof CandidateFilters) => (items: SelectOption[] | boolean) => {
    setFilters(current => ({ ...current, [field]: items }));
    setTableOptions(current => ({ ...current, offset: 0, page: 1 }));
  };
  const onChangeWithActions = (e: { target: { checked: boolean } }) =>
    onCandidateFilterChange('only_with_actions')(e.target.checked);

  const onExpand =
    ({ uuid }: Record<string, unknown>) =>
    () => {
      if (!expandedRowKeys.includes(uuid as string)) setExpandedRowKeys(rows => [...rows, uuid as string]);
      else setExpandedRowKeys(rows => rows.filter(r => r !== uuid));
    };

  const onGroupingChange = (newGrouping: GroupingOption) => {
    sessionStorage.setItem('activeCandidatesGrouping', newGrouping.value);
    setGrouping(g => ({ ...newGrouping, tableData: g.tableData }));
  };

  const getTableData = () => {
    setLoading(true);
    setTabsLoading(true);
    getCandidatesTableDashboardAPI(
      dashboard,
      {
        limit: tableOptions.limit.value,
        offset: tableOptions.offset,
        sorting_field: tableOptions.sorting.field,
        sorting: tableOptions.sorting.order,
        only_with_actions: filters.only_with_actions,
        recruiters: filters.recruiters.map(recruiter => recruiter.value as string),
        ...(recruiterDashboard
          ? {
              steps: filters.steps.map(step => step.value as string),
              actions: filters.actions.map(action => action.value as string),
            }
          : {}),
        search: searchQuery,
      },
      grouping.value
    )
      .then(d => {
        setTableData([]);
        setTableOptions(o => ({ ...o, itemsCount: d.count }));
        setGrouping(g => ({ ...g, tableData: g.value }));
        setTableData(
          CANDIDATES_TABLES_MAPPERS[grouping.value](
            d.results as unknown as Record<string, unknown>[],
            user as unknown as Record<string, unknown>
          ) as Record<string, unknown>[]
        );
        setExpandedRowKeys(d.results.map((_: unknown, index: number) => index));

        const docsUniqueUUIDs = Array.from(
          new Set(
            grouping.value
              ? (d.results as unknown as Record<string, unknown>[]).flatMap(r =>
                  ((r.candidates as Record<string, unknown>[]) || []).flatMap(item =>
                    Array.isArray(item.cvs) ? item.cvs : []
                  )
                )
              : (d.results as unknown as Record<string, unknown>[]).flatMap(item =>
                  Array.isArray(item.cvs) ? item.cvs : []
                )
          )
        ).filter(Boolean) as string[];
        if (docsUniqueUUIDs.length)
          getDocumentsByUUID(docsUniqueUUIDs, 'dashboard-active-candidates-documents')
            .then(cvs => {
              if (!Array.isArray(cvs)) return;
              setTableData(data =>
                data.map(row =>
                  Array.isArray(row.candidates)
                    ? {
                        ...row,
                        candidates: row.candidates.map(candidate => ({
                          ...candidate,
                          cvs: [
                            ...(Array.isArray(candidate.cvs) ? candidate.cvs : []).map(
                              cv => cvs.find(c => c.uuid === cv.uuid) || cv
                            ),
                            ...(Array.isArray(candidate.cvs_links) ? candidate.cvs_links : []).map(file => ({
                              uuid: file.uuid,
                              file,
                              filename: file,
                              link: true,
                            })),
                          ],
                        })),
                      }
                    : {
                        ...row,
                        cvs: [
                          ...(Array.isArray(row.cvs) ? row.cvs : []).map(cv => cvs.find(c => c.uuid === cv.uuid) || cv),
                          ...(Array.isArray(row.cvs_links) ? row.cvs_links : []).map(file => ({
                            uuid: file.uuid,
                            file,
                            filename: file,
                            link: true,
                          })),
                        ],
                      }
                )
              );
            })
            .catch(() => {});
      })
      .catch((error: { code?: string }) => {
        if (error?.code === 'ERR_CANCELED') return;
        setTableData([]);
        setGrouping(g => ({ ...g, tableData: g.value }));
        setTableOptions(INITIAL_VALUES);
      })
      .finally(() => {
        setLoading(false);
        setTabsLoading(false);
      });
  };

  const onActionClick = (record: Record<string, unknown>) => () => {
    const action = record.action as Record<string, unknown>;
    const button = action.button as Record<string, unknown> | undefined;
    const statusStep = record.status_step as Record<string, unknown>;
    const candidate = record.candidate as Record<string, unknown>;
    const job = record.job as Record<string, unknown>;

    switch (button?.label) {
      case 'Submit Decision':
      case 'Submit Final Decision':
        if (record.opening)
          dispatch(getOpening(job.uuid as string, (record.opening as Record<string, unknown>).opening_id as string));
        if ((statusStep.step_number as number) >= 6) {
          dispatch(getHiredCandidate(job.uuid as string, candidate.uuid as string));
          setOfferDecisionPopup({
            open: true,
            userData: {
              full_name_latin: candidate.full_name_latin,
              full_name_local: candidate.full_name_local,
              speciality: '',
              link: `/jobs/${job.uuid}/show/steps/${job.step_uuid}/${candidate.uuid}`,
              offer_decision: record.offer_decision,
            },
            activeStep: statusStep.step_number,
            jobId: job.uuid,
            stepId: job.step_uuid,
            candidateId: candidate.uuid,
          });
        } else {
          setDecisionPopup({
            open: true,
            activeStep: statusStep.step_number,
            isFinalDecision: button?.isFinalDecision,
            userData: {
              full_name_latin: candidate.full_name_latin,
              full_name_local: candidate.full_name_local,
              speciality: '',
            },
            jobId: job.uuid,
            stepId: job.step_uuid,
            candidateId: candidate.uuid,
            body: {
              candidate: candidate.uuid,
              comment: '',
              positive: false,
              step: job.step_uuid,
            },
            link: `/jobs/${job.uuid}/show/steps/${job.step_uuid}/${candidate.uuid}`,
          });
        }
        break;
      case 'Schedule interview':
        setInterviewPopup({
          open: true,
          candidateName: candidate.full_name_latin,
          empty: true,
          jobId: job.uuid,
          stepId: job.step_uuid,
          candidateId: candidate.uuid,
          userData: {
            full_name_latin: candidate.full_name_latin,
            full_name_local: candidate.full_name_local,
            speciality: '',
            link: `/jobs/${job.uuid}/show/steps/${job.step_uuid}/${candidate.uuid}`,
          },
        });
        break;
      case 'Provide feedback':
        getInterviewFeedbacksDashboardAPI(
          job.uuid as string,
          job.step_uuid as string,
          candidate.uuid as string,
          user.uuid
        ).then(feedbacks => {
          const feedback = feedbacks.find(f => f.approver.uuid === user.uuid);
          setFeedbackPopup({
            open: true,
            feedback: { ...feedback, comment_initial: feedback!.comment },
            link: `/jobs/${job.uuid}/show/steps/${job.step_uuid}/${candidate.uuid}`,
            userData: {
              link: `/jobs/${job.uuid}/show/steps/${job.step_uuid}/${candidate.uuid}`,
              full_name_latin: candidate.full_name_latin,
              full_name_local: candidate.full_name_local,
              speciality: '',
            },
            jobId: job.uuid,
            stepId: job.step_uuid,
            candidateId: candidate.uuid,
            scorecardId: feedback!.uuid,
          });
        });
        break;
      case 'Add Offer':
        setOfferPopup({
          open: true,
          userData: {
            link: `/jobs/${job.uuid}/show/steps/${job.step_uuid}/${candidate.uuid}`,
            full_name_latin: candidate.full_name_latin,
            full_name_local: candidate.full_name_local,
            speciality: '',
          },
          jobId: job.uuid,
          stepId: job.step_uuid,
          candidateId: candidate.uuid,
        });
        break;
      default:
    }
  };

  const onCloseDecision = () => {
    setDecisionPopup({
      open: false,
      activeStep: 0,
      isFinalDecision: false,
      userData: {},
      jobId: '',
      stepId: '',
      candidateId: '',
      body: {},
      link: '',
    });
  };

  const onSaveDecision = (_isFinal: unknown, data: Record<string, unknown>) => {
    const body = { ...data };
    if (body.positive) delete body.reason;
    dispatch(
      submitDecision(
        decisionPopup.jobId as string,
        decisionPopup.stepId as string,
        decisionPopup.candidateId as string,
        body,
        decisionPopup.isFinalDecision as boolean
      )
    )
      .then(() => {
        onCloseDecision();
        getTableData();
      })
      .catch(() => {
        notification({
          message: 'The content is out of date, please refresh the page',
          type: NOTIFICATION_TYPES.ERROR,
        });
      });
  };

  const onCloseInterview = () => {
    setInterviewPopup({
      open: false,
      candidateName: '',
      empty: true,
      userData: {},
    });
  };

  const onSaveInterview = (data: Record<string, unknown>) => {
    dispatch(
      createInterview(
        interviewPopup.jobId as string,
        interviewPopup.stepId as string,
        interviewPopup.candidateId as string,
        {
          ...data,
          locations: (data.locations as Array<{ value: string; name?: string }>).map(
            ({ value }) => value
          ) as unknown as {
            id: string;
            name: string;
          }[],
          create_google_event: !!data.create_google_event,
          date: (data.date as moment.Moment).format('YYYY-MM-DD'),
          type: (data.type as SelectOption).value as string,
          from_time: utcTime(((data.from_time as SelectOption).value as string) || '12:00'),
          to_time: utcTime(((data.to_time as SelectOption).value as string) || '13:00'),
        }
      )
    )
      .then(() => {
        onCloseInterview();
        getTableData();
      })
      .catch(() => {
        notification({
          message: 'The content is out of date, please refresh the page',
          type: NOTIFICATION_TYPES.ERROR,
        });
      });
  };

  const onImproveFeedbackComment = (body: ImproveInterviewFeedbackCommentPayload, signal: AbortSignal) =>
    improveInterviewFeedbackComment(
      feedbackPopup.jobId as string,
      feedbackPopup.stepId as string,
      feedbackPopup.candidateId as string,
      body,
      signal
    );

  const onCloseFeedback = () => {
    setFeedbackPopup({
      open: false,
      feedback: undefined,
      userData: {},
      jobId: '',
      stepId: '',
      candidateId: '',
      scorecardId: '',
    });
  };

  const onSaveFeedback = (data: Record<string, unknown>) => {
    saveInterviewFeedbacks(
      feedbackPopup.jobId as string,
      feedbackPopup.stepId as string,
      feedbackPopup.candidateId as string,
      feedbackPopup.scorecardId as string,
      {
        ...data,
        english_level: (data.english_level as SelectOption | undefined)?.value as string,
        level: (data.level as SelectOption | undefined)?.value as string,
      }
    ).then(() => {
      onCloseFeedback();
      getTableData();
    });
  };

  const onCloseOffer = () => {
    setOfferPopup({ open: false, userData: {}, jobId: '', stepId: '', candidateId: '' });
  };

  const onSaveOffer = (data: Record<string, unknown>) => {
    dispatch(
      submitOffer(offerPopup.jobId as string, offerPopup.stepId as string, offerPopup.candidateId as string, data)
    )
      .then(() => {
        onCloseOffer();
        getTableData();
      })
      .catch(() => {
        notification({
          message: 'The content is out of date, please refresh the page',
          type: NOTIFICATION_TYPES.ERROR,
        });
      });
  };

  const onCloseSubmitOfferPopup = () => {
    setOfferDecisionPopup({ open: false, userData: {}, activeStep: 0, jobId: '', stepId: '', candidateId: '' });
  };

  const onSubmitOfferPopup = (body: Record<string, unknown>) => {
    dispatch(
      submitDecisionWithOppening(
        offerDecisionPopup.jobId as string,
        offerDecisionPopup.stepId as string,
        offerDecisionPopup.candidateId as string,
        body
      )
    )
      .then(() => {
        onCloseSubmitOfferPopup();
        getTableData();
      })
      .catch(() => {
        notification({
          message: 'The content is out of date, please refresh the page',
          type: NOTIFICATION_TYPES.ERROR,
        });
      });
  };

  useEffect(() => {
    getListUsersByRole({
      role: [ROLES.RECRUITER, ROLES.RECRUITER_LEAD, ROLES.HR_LEAD],
    })
      .then(users => {
        setRecruitersOptions(users.map(u => ({ value: u.uuid, label: u.full_name })));
      })
      .catch(() => {
        setRecruitersOptions([]);
      });
  }, []);

  useEffect(getTableData, [
    tableOptions.sorting,
    tableOptions.limit,
    tableOptions.offset,
    tableOptions.page,
    grouping.value,
    filters,
    searchQuery,
  ]);
  useScreenResize('md', tableRef, !grouping.tableData);

  return (
    <>
      <Table
        innerRef={tableRef}
        rowKey="uuid"
        className={cn('active-candidates', grouping.tableData)}
        cardTitle="Active candidates"
        cardTitleExtra={
          <>
            <Checkbox
              label="Show only with action"
              isSelected={filters.only_with_actions}
              onChange={onChangeWithActions}
            />
            <div className="filter-container">
              <SelectSmall
                label="Group by"
                options={CANDIDATES_GROUP}
                value={grouping}
                onChange={onGroupingChange}
                placement={PLACEMENTS.BOTTOM_RIGHT}
              />
            </div>
          </>
        }
        cardCount={tableOptions.itemsCount}
        emptyComponent={<ActiveCandidatesTableEmpty />}
        extra={
          <>
            <Input
              type="search"
              className="name-search"
              placeholder="Search by name or job"
              value={search}
              delay={300}
              onChange={e => setSearch(e.target.value)}
              onDebounceChange={e => setDebouncedSearch(e.target.value)}
            />
            {leadDashboard && (
              <Select
                className="active-candidates__filter"
                mode={SELECT_TYPES.MULTIPLE}
                value={filters.recruiters}
                onChange={onCandidateFilterChange('recruiters')}
                options={recruitersOptions as AtomSelectOption[]}
                optionFilterProp="label"
                placeholder="Select recruiter"
              />
            )}
            {recruiterDashboard && (
              <>
                <Select
                  className="active-candidates__filter"
                  mode={SELECT_TYPES.MULTIPLE}
                  value={filters.steps}
                  onChange={onCandidateFilterChange('steps')}
                  options={STEPS as AtomSelectOption[]}
                  optionFilterProp="label"
                  placeholder="Select step"
                />
                <Select
                  className="active-candidates__filter"
                  mode={SELECT_TYPES.MULTIPLE}
                  value={filters.actions}
                  onChange={onCandidateFilterChange('actions')}
                  options={CANDIDATE_ACTION_OPTIONS as AtomSelectOption[]}
                  optionFilterProp="label"
                  placeholder="Select action"
                />
              </>
            )}
          </>
        }
        columns={
          (CANDIDATES_TABLE_COLUMNS as Record<string, Array<(...args: unknown[]) => unknown>>)[grouping.tableData][0](
            tableOptions.sorting,
            setSorting,
            leadDashboard,
            user.uuid,
            expandedCVS,
            setExpandedCVS,
            onActionClick
          ) as TableColumnType[]
        }
        loading={loading}
        data={tableData as TableRow[]}
        limit={tableOptions.limit}
        page={tableOptions.page}
        itemsCount={tableOptions.itemsCount}
        tooltipInfo="One candidate may be mentioned more than once (for example, if assigned to more than one job)"
        updateParams={onChangeFilter}
        expandable={
          grouping.tableData && {
            expandedRowKeys,
            // eslint-disable-next-line react/no-unstable-nested-components
            expandedRowRender: ({ candidates }: Record<string, unknown>) => (
              <>
                <Table
                  rowKey="uuid"
                  className={cn('active-candidates__sub-table', grouping.tableData)}
                  columns={
                    (CANDIDATES_TABLE_COLUMNS as Record<string, Array<(...args: unknown[]) => unknown>>)[
                      grouping.tableData
                    ][1](user.uuid, leadDashboard, expandedCVS, setExpandedCVS, onActionClick) as TableColumnType[]
                  }
                  data={candidates as TableRow[]}
                  showCardHeader={false}
                  showCardFooter={false}
                />
                <TableMobile
                  className={cn('active-candidates__sub-table active-candidates__sub-table-mobile', grouping.tableData)}
                  data={candidates as TableRow[]}
                  columns={
                    (CANDIDATES_TABLE_COLUMNS as Record<string, Array<(...args: unknown[]) => unknown>>)[
                      grouping.tableData
                    ][1](user.uuid, leadDashboard, expandedCVS, setExpandedCVS, onActionClick) as TableColumnType[]
                  }
                />
              </>
            ),
            // eslint-disable-next-line react/no-unstable-nested-components
            expandIcon: ({ expanded, record }) => (
              <div role="none" className="expanded-row">
                <div className="expanded-row__info">
                  <Vector className={cn('expanded-row__icon', { expanded })} onClick={onExpand(record)} />
                  <Typography.Text className="name">
                    {record.name}
                    <span>{record.count}</span>
                  </Typography.Text>
                  {!!record.extra && <Typography.Text className="extra">{record.extra}</Typography.Text>}
                </div>
              </div>
            ),
          }
        }
        defaultTableMobile={!grouping.tableData}
      />
      <PopupSubmitDecision
        open={!!decisionPopup.open}
        onSave={onSaveDecision}
        onClose={onCloseDecision}
        isFinalDecision={decisionPopup.isFinalDecision as boolean}
        userData={decisionPopup.userData as Record<string, unknown>}
        activeStep={(decisionPopup.activeStep as number) - 1}
        candidateLink={decisionPopup.link as string}
      />
      <PopupCreateInterview
        open={!!interviewPopup.open}
        onOk={values => onSaveInterview(values as Record<string, unknown>)}
        onClose={onCloseInterview}
        initialValues={interviewInitialValues}
        empty={interviewPopup.empty as boolean}
        {...({ candidateName: interviewPopup.candidateName } as Record<string, unknown>)}
        accessToCalendar={user.profile.has_calendar_access}
        userData={interviewPopup.userData as Record<string, unknown>}
      />
      <PopupCreateFeedback
        open={!!feedbackPopup.open}
        feedback={feedbackPopup.feedback as InterviewFeedback | undefined}
        onSave={onSaveFeedback}
        onImproveComment={onImproveFeedbackComment}
        onClose={onCloseFeedback}
        userData={feedbackPopup.userData as Record<string, unknown>}
      />
      <PopupCreateOffer
        open={!!offerPopup.open}
        onSave={onSaveOffer}
        onClose={onCloseOffer}
        userData={offerPopup.userData as Record<string, unknown>}
      />
      <PopupSubmitOfferDecision
        open={!!offerDecisionPopup.open}
        userData={offerDecisionPopup.userData as Record<string, unknown>}
        onClose={onCloseSubmitOfferPopup}
        onSubmit={onSubmitOfferPopup}
        isOnboarding={offerDecisionPopup.activeStep === 7}
        candidateLink={(offerDecisionPopup.userData as Record<string, unknown>).link as string}
        jobId={offerDecisionPopup.jobId as string}
      />
    </>
  );
}

export const DashboardHRLead = () => {
  const query = useQuery();

  const [newJobsTrigger, setNewJobsTrigger] = useState<Date | undefined>();
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState((query.tab as string) || 'openings');
  const tabs = useTabs(loading);

  return (
    <div className="dashboard-hr-lead">
      <NewJobs trigger={newJobsTrigger} isHrl />
      <Tabs className="dashboard-hr-lead__tabs" activeTab={tab} onChange={setTab} items={tabs} />
      {
        {
          [tabs[0].key]: <AllOpeningsService setTabsLoading={setLoading} onChangeTrigger={setNewJobsTrigger} />,
          [tabs[1].key]: <MyActiveCandidates leadDashboard setTabsLoading={setLoading} />,
          [tabs[2].key]: <SelfSubmittedCandidatesDashboardTab setTabsLoading={setLoading} dashboard="hr-lead" />,
        }[tab]
      }
    </div>
  );
};
